import { OktaContext } from '@okta/okta-react';
import { useNavigate } from '@rocketlane/rocket-router';
import { Space } from 'antd';
import { FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PageLoading } from 'components/atoms/loading';
import {
  ProductiveHeading03,
  ProductiveHeading04,
  ProductiveHeading05,
} from 'components/styles';
import { RoutePaths } from 'constant';
import { useAccountMetaQuery } from 'queries';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { CompanyLogo } from './common';

const Container = styled.div`
  padding: var(--spacing-12);
  text-align: center;
  display: flex;
  width: 100%;
  min-width: 420px;
  min-height: 100vh;
  min-height: 100dvh;
  justify-content: center;
  align-items: center;
  background: var(--page-background, #ececec);
`;

const Heading = styled.div`
  ${ProductiveHeading04};
`;

const Strong = styled.strong`
  font-weight: var(--font-weight-bold);
  padding-left: var(--spacing-03);
`;

const Message = styled.div`
  ${ProductiveHeading03};
`;
const Greeting = styled.div`
  ${ProductiveHeading05};
`;

const StyledButton = styled.button`
  color: var(--blue-60);
  cursor: pointer;
  background-color: transparent;
  border: none;
`;

export const GoodbyePage: FC<React.PropsWithChildren<unknown>> = () => {
  useDismissBootSkeleton();

  const [timeLeft, setTimeLeft] = useState(10);
  const oktaContext = useContext(OktaContext);
  const authState = oktaContext?.authState;
  const { data, isLoading } = useAccountMetaQuery();
  const push = useNavigate();

  const isAuthenticated = authState?.isAuthenticated;

  useEffect(() => {
    if (isAuthenticated) {
      push(RoutePaths.logout, { replace: true });
    }
  }, [isAuthenticated, push]);

  // When cookies are enabled, skip the timer and auto-redirect
  useEffect(() => {
    // When cookies are enabled, skip the timer and auto-redirect
    if (data?.okta?.cookiesEnabled === true && data?.okta?.clientId) {
      return () => {};
    }

    const interval = setInterval(() => {
      if (timeLeft > 0) {
        setTimeLeft(timeLeft - 1);
      } else if (timeLeft === 0) {
        clearInterval(interval);

        if (data?.okta?.cookiesEnabled === true && data?.okta?.clientId) {
          redirectToOAuth(data.okta.clientId);
        } else {
          push(RoutePaths.login);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, push, data?.okta?.cookiesEnabled, data?.okta?.clientId]);

  const handleReturnToLogin = () => {
    if (data?.okta?.cookiesEnabled === true && data?.okta?.clientId) {
      redirectToOAuth(data.okta.clientId);
    } else {
      push(RoutePaths.login);
    }
  };

  return isLoading ? (
    <PageLoading />
  ) : (
    <Container>
      <Space size={80} direction="vertical">
        <Space size={48} direction="vertical">
          <Space size={96} direction="vertical">
            <Space size={24}>
              {data && <CompanyLogo data={data.company} />}
              <Heading>
                {data?.company.companyName}
                <Strong>
                  <FormattedMessage id="onboarding" />
                </Strong>
              </Heading>
            </Space>
            <Message>
              <FormattedMessage id="goodbye.logged_out" />
            </Message>
          </Space>
          <Greeting>
            <FormattedMessage id="goodbye.great_day" />
          </Greeting>
        </Space>
        <Space direction="vertical" size={12}>
          {data?.okta?.cookiesEnabled === true &&
          data?.okta?.clientId ? null : (
            <div>
              <FormattedMessage id="goodbye.redirect" values={{ timeLeft }} />
            </div>
          )}
          <div>
            <StyledButton type="button" onClick={handleReturnToLogin}>
              <FormattedMessage id="goodbye.return_login" />
            </StyledButton>
          </div>
        </Space>
      </Space>
    </Container>
  );
};
